import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fmtINR, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { sanitizeGstin } from "../lib/constants";
import { LedgerPostingPicker } from "../components/LedgerFormModal";
import { UploadSimple, Robot, ArrowsClockwise, FloppyDisk, Warning, FilePdf } from "@phosphor-icons/react";

export default function PurchaseBillUploadPage() {
  const fileRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [res, setRes] = useState(null);
  const [form, setForm] = useState(null);
  const [createVendor, setCreateVendor] = useState(true);
  const [saved, setSaved] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setAnalyzing(true); setRes(null); setForm(null); setSaved(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/purchase-bills/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setRes(data);
      setForm({ ...data.extracted, itc_type: "OTH", notes: "" });
      toast.success("Bill read by AI — review and save");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setAnalyzing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/purchase-bills/confirm", {
        ...form,
        vendor_id: res.vendor_id || null,
        create_vendor: !res.vendor_exists && createVendor,
        purchase_type: res.purchase_type,
        file_id: res.file_id,
        taxable: Number(form.taxable || 0), cgst: Number(form.cgst || 0), sgst: Number(form.sgst || 0),
        igst: Number(form.igst || 0), cess: Number(form.cess || 0),
      });
      setSaved(data);
      toast.success(`Inward bill ${data.bill_no} saved`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const total = form ? Number(form.taxable || 0) + Number(form.cgst || 0) + Number(form.sgst || 0) + Number(form.igst || 0) + Number(form.cess || 0) : 0;

  return (
    <div data-testid="purchase-bill-upload-page">
      <PageHeader
        title="Upload Purchase Bill (AI)"
        subtitle="Upload a vendor bill (PDF or photo). AI extracts vendor, GST split and items — you review and save into Inward Bills (ITC)."
      />
      <div className="p-8 max-w-4xl space-y-6">
        <div className="card-base p-5 flex items-center gap-4 flex-wrap">
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="pb-file-input" onChange={e => upload(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={analyzing} className="btn-primary inline-flex items-center gap-1.5" data-testid="pb-upload-btn">
            {analyzing ? <ArrowsClockwise size={14} weight="bold" className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
            {analyzing ? "AI reading the bill…" : "Upload Bill (PDF / Photo)"}
          </button>
          <div className="text-xs text-ink-muted flex items-center gap-1.5"><Robot size={14} /> Same-state vs ex-state is detected from the GSTIN automatically</div>
        </div>

        {res && form && !saved && (
          <div className="card-base p-6 space-y-4" data-testid="pb-review-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-display font-semibold">Review Extracted Bill</div>
              <div className="flex items-center gap-2 text-xs">
                {res.purchase_type && <span className={`status-pill ${res.purchase_type === "intra_state" ? "status-success" : "status-info"}`} data-testid="pb-purchase-type">{res.purchase_type === "intra_state" ? "Same-State Purchase (CGST+SGST)" : "Ex-State Purchase (IGST)"}</span>}
                {typeof res.confidence === "number" && <span className="status-pill status-neutral">AI confidence {res.confidence}%</span>}
                <a href={`${API}/files/${res.file_id}`} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-1 text-xs"><FilePdf size={14} /> View file</a>
              </div>
            </div>

            {res.possible_duplicate && (
              <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="pb-duplicate-warning">
                <Warning size={16} className="text-amber-600" /> A bill with this number already exists ({res.duplicate_of?.bill_no} · {res.duplicate_of?.vendor_name}). Check before saving.
              </div>
            )}
            {(res.warnings || []).map((w, i) => (
              <div key={w} className="text-xs text-amber-700 flex items-center gap-1.5"><Warning size={13} /> {w}</div>
            ))}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="label-overline block mb-1.5">Vendor Name *</div>
                <input className="field" data-testid="pb-vendor-name" value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} />
                {res.vendor_exists
                  ? <div className="text-xs text-[#1C4F32] mt-1" data-testid="pb-vendor-exists">✓ Matched to existing vendor master</div>
                  : <label className="flex items-center gap-1.5 text-xs mt-1 cursor-pointer"><input type="checkbox" checked={createVendor} onChange={e => setCreateVendor(e.target.checked)} data-testid="pb-create-vendor" /> Add this vendor to masters</label>}
              </div>
              <div>
                <div className="label-overline block mb-1.5">Vendor GSTIN</div>
                <input className="field font-mono" data-testid="pb-vendor-gstin" value={form.vendor_gstin} onChange={e => setForm({ ...form, vendor_gstin: sanitizeGstin(e.target.value) })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">Bill No *</div>
                <input className="field font-mono" data-testid="pb-bill-no" value={form.bill_no} onChange={e => setForm({ ...form, bill_no: e.target.value })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">Bill Date *</div>
                <input type="date" className="field" data-testid="pb-bill-date" value={form.bill_date} onChange={e => setForm({ ...form, bill_date: e.target.value })} />
              </div>
              {["taxable", "cgst", "sgst", "igst", "cess"].map(k => (
                <div key={k}>
                  <div className="label-overline block mb-1.5">{k.toUpperCase()} (₹)</div>
                  <input type="number" step="0.01" className="field text-right" data-testid={`pb-${k}`} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} />
                </div>
              ))}
              <div>
                <div className="label-overline block mb-1.5">Total</div>
                <div className="font-display text-xl font-bold tabular pt-1.5" data-testid="pb-total">{fmtINR(total)}</div>
              </div>
              <LedgerPostingPicker testPrefix="pb" drId={form.dr_ledger_id} crId={form.cr_ledger_id} vendorName={form.vendor_name} purchaseType={res.purchase_type}
                onChange={(patch) => setForm(f => ({ ...f, ...patch }))} />
            </div>

            {(form.items || []).length > 0 && (
              <div className="table-wrapper">
                <table className="table-base">
                  <thead><tr><th>Item</th><th>HSN</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">GST %</th><th className="text-right">Amount</th></tr></thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={`${it.name}-${it.hsn}-${i}`}><td>{it.name}</td><td className="font-mono text-xs">{it.hsn || "—"}</td><td className="text-right tabular">{it.qty}</td><td className="text-right tabular">{fmtINR(it.rate)}</td><td className="text-right tabular">{it.gst_rate}%</td><td className="text-right tabular">{fmtINR(it.amount)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button onClick={save} disabled={saving || !form.vendor_name || !form.bill_no || !form.bill_date} className="btn-primary inline-flex items-center gap-1.5" data-testid="pb-save-btn">
                <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save Inward Bill"}
              </button>
            </div>
          </div>
        )}

        {saved && (
          <div className="card-base p-6 text-sm space-y-2" data-testid="pb-saved-card">
            <div className="font-display font-semibold text-[#1C4F32]">✓ Saved: {saved.bill_no} · {saved.vendor_name} · {fmtINR(saved.total)}</div>
            <div className="flex gap-3">
              <Link to="/inward-bills" className="btn-secondary text-xs">Open Inward Bills (ITC)</Link>
              <button onClick={() => { setSaved(null); setRes(null); setForm(null); }} className="btn-primary text-xs" data-testid="pb-upload-another">Upload Another Bill</button>
            </div>
          </div>
        )}
      </div>
      <FieldStyle />
    </div>
  );
}
